import { toastServerError } from "helpers";
import { t } from "i18next";
import { ApiError, ApiServiceError } from "libs/api";
import { useDirectUploadToS3 } from "libs/directUpload";
import { toast } from "react-hot-toast";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { useCreateProduct, useUpdateProduct } from "../apis";
import { ProductFormValues } from "../components";
import {
  convertFormDataToCreateRequestBody,
  convertFormDataToUpdateRequestBody,
  getProductFormVariantsWithDefaultPreviewIds
} from "../helpers";

const useProductDetailsFormSubmit = (defaultValues: ProductFormValues) => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const { uploader } = useDirectUploadToS3();

  const { subChildCategoryId, editableProductId } = useParams();

  const onSaveError = (error: ApiServiceError<ApiError>) => {
    toastServerError(error);
  };

  const { isLoading: isLoadingCreateProduct, mutate: mutateCreateProduct } =
    useCreateProduct({
      onSuccess: () => {
        navigate(`/products`);
        toast.success(t("Product was created successfully"));
      },
      onError: onSaveError
    });

  const { isLoading: isLoadingUpdateProduct, mutate: mutateUpdateProduct } =
    useUpdateProduct({
      onSuccess: () => {
        if (state?.canGoBack) navigate(-1);
        else navigate("/products");

        toast.success(t("Product was updated successfully"));
      },
      onError: onSaveError
    });

  const onSubmit = async (formData: ProductFormValues) => {
    try {
      const variantsWithCompressedPreviewId =
        await getProductFormVariantsWithDefaultPreviewIds(
          formData.variants,
          defaultValues.variants,
          uploader
        );

      if (
        variantsWithCompressedPreviewId.some(
          (variant) => !variant.compressedPreviewId
        )
      ) {
        toast.error(t("Something went wrong with default preview uploading"));
        return;
      }

      if (subChildCategoryId && !editableProductId) {
        const body = await convertFormDataToCreateRequestBody(
          { ...formData, variants: variantsWithCompressedPreviewId },
          subChildCategoryId
        );

        mutateCreateProduct(body);
      } else if (editableProductId) {
        const body = convertFormDataToUpdateRequestBody(
          { ...formData, variants: variantsWithCompressedPreviewId },
          editableProductId,
          defaultValues
        );
        mutateUpdateProduct(body);
      }
    } catch (error) {
      // oxlint-disable-next-line no-console
      console.error("Product submit failed", error);
      toast.error(t("Something went wrong while saving the product"));
    }
  };

  return {
    onSubmit,
    isSubmitLoading: isLoadingCreateProduct || isLoadingUpdateProduct
  };
};

export default useProductDetailsFormSubmit;
